<script setup lang="ts">
const props = defineProps({
  craftable: {
    type: Object as PropType<CraftItem>,
    required: true,
  },
});

const { getMerchantAttributes } = useLuxuryDesign();
const craftableDesign = computed(() =>
  getMerchantAttributes(props.craftable.merchantId)
);

const isLuxuryDesign =
  craftableDesign.value["data-merchant-design"] === "luxury";

const isCraftableComingSoon = computed(() => {
  if (props.craftable.start) {
    const startDate = new Date(props.craftable.start);
    const now = new Date();
    return startDate > now;
  }
  return false;
});
const isCraftableStartShowable = computed(() => {
  if (props.craftable.start) {
    const startDate = new Date(props.craftable.start);
    const maxShowableYear = new Date(2099, 1);
    return startDate < maxShowableYear;
  }
  return false;
});
const isHovered = ref<boolean>(false);
</script>
<template>
  <div
    v-bind="craftableDesign"
    class="relative grid grid-rows-[1fr,1fr,1fr,1fr, 1fr] border rounded-lg border-primary-900 will-change-transform transition ease-in-out duration-300 overflow-hidden collectible-card"
    :class="{ '-translate-y-1': isHovered }"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false">
    <div class="relative overflow-hidden">
      <CardImage :src="getPortraitItemImage(craftable.imageId)" />
      <div
        v-if="craftable.level"
        class="absolute top-2 right-2 text-2xl text-neutral-200 font-bold text-center w-fit rounded-lg flex items-center justify-center">
        <BaseIconHexagon class="w-10 h-10" />
        <p class="absolute z-10">{{ craftable.level }}</p>
      </div>
      <div
        v-if="craftable.number != null && craftable.number !== undefined && !isCraftableComingSoon"
        class="absolute bottom-4 right-0 text-xs text-neutral-200 text-center font-bold w-fit px-4 py-1 rounded-tl-2xl rounded-bl z-20"
        :class="craftable.number <= 10 ? 'bg-error-300' : 'bg-neutral-500'">
        <template v-if="craftable.number === 0">
          {{ $t("main.cardCollectible.soldOut") }}
        </template>
        <template v-else-if="craftable.number <= 10">
          {{
            $t("main.cardCollections.unitsLeft", {
              units: craftable.number,
              copies: craftable.totalCopies,
            })
          }}
        </template>
        <template v-else>
          {{ $t("main.cardCollections.supply") }}
          {{ craftable.number }} <span class="font-[Roboto] font-black">/</span>
          {{ craftable.totalCopies }}
        </template>
      </div>
    </div>

    <div
      v-if="!isLuxuryDesign"
      class="w-full grid grid-rows-[4px_2px] z-10 collectible-card__rarity">
      <div :class="getRarityColor(craftable.rarity!, 'bg')"></div>
      <div
        class="opacity-80"
        :class="getRarityColor(craftable.rarity!, 'bg')"></div>
    </div>
    <div class="px-2 py-3.5 text-center truncate merchant-card__surface">
      <h1 class="text-neutral-200 text-lg font-bold truncate merchant-heading">
        {{ $t(`${craftable.translationId}.title`) }}
        {{ $t(`${craftable.translationId}.subtitle`) }}
      </h1>
    </div>
    <div
      v-if="craftable.start && isCraftableComingSoon"
      class="pt-3 pb-3 border-t border-primary-900 px-2 py-2.5 flex justify-center items-center merchant-card__surface">
      <p
        class="text-secondary-200 font-semibold text-md pt-1 text-center w-full">
        {{ $t("main.home.comingSoon") }}
      </p>
    </div>
    <div
      v-if="isCraftableStartShowable && isCraftableComingSoon"
      class="text-xs flex items-center gap-1 py-3 px-2 overflow-hidden rounded-b-lg bg-neutral-900 border-t border-primary-900">
      <NuxtIcon name="mdi:clock-outline" class="text-neutral-200 w-6 h-6" />
      <p class="text-secondary-200">{{ $t(`main.availableOn`) }}</p>
      <p class="text-neutral-200 font-semibold">
        {{
          new Date(craftable.start).toLocaleDateString($i18n.locale, {
            month: "long",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        }}
      </p>
    </div>
    <div
      v-else
      class="pt-3.5 pb-2.5 border-t border-primary-900 px-2 py-2.5 flex justify-between merchant-card__surface">
      <p class="text-secondary-200 font-semibold text-xs pt-1 overflow-hidden">
        {{ $t("main.cardCraftable.requires") }}
      </p>
      <CardCraftList
        :crafting-requirements="craftable.ingredients"
        :show-requirements="craftable.showRequirements" />
    </div>
  </div>
</template>
